import { motion } from 'framer-motion';

import { DEFAULT_GREETING_MESSAGE, GREETING_TITLE } from './constants';
import { NOVA_DESKTOP_LAYOUT, NOVA_MOBILE_LAYOUT } from './layout';
import { getFadeScaleMotionProps, getFadeUpMotionProps } from './motion';
import { NovaBackgroundBlob, NovaRobotIcon } from './nova-empty-state-art';

interface NovaEmptyStateProps {
  descriptionId: string;
  isMobile: boolean;
  message?: string;
  prefersReducedMotion: boolean;
  titleId: string;
}

export function NovaEmptyState({
  descriptionId,
  isMobile,
  message,
  prefersReducedMotion,
  titleId,
}: NovaEmptyStateProps) {
  const layout = isMobile ? NOVA_MOBILE_LAYOUT : NOVA_DESKTOP_LAYOUT;

  return (
    <section
      key="overview"
      aria-describedby={descriptionId}
      aria-labelledby={titleId}
      className="mx-auto flex size-full min-w-0 flex-col items-center justify-center text-center"
      data-testid="nova-empty-state"
    >
      <motion.div
        className="relative"
        data-testid="nova-empty-state-frame"
        style={{
          width: layout.frameWidth,
          height: layout.frameHeight,
          padding: layout.framePadding,
          boxSizing: 'border-box',
        }}
        {...getFadeScaleMotionProps(prefersReducedMotion)}
      >
        <div
          className="relative mx-auto"
          data-testid="nova-empty-state-content"
          style={{ width: layout.innerWidth, height: layout.innerHeight }}
        >
          <NovaBackgroundBlob
            className="absolute"
            data-testid="nova-empty-state-blob"
            style={{
              top: layout.blobTop,
              left: '50%',
              transform: 'translateX(-50%) rotate(5.89deg)',
              width: layout.blobWidth,
              height: layout.blobHeight,
            }}
          />

          <div
            aria-hidden="true"
            className="absolute z-10"
            data-testid="nova-empty-state-icon"
            style={{
              top: layout.iconTop,
              left: '50%',
              width: layout.iconFrameSize,
              height: layout.iconFrameSize,
              transform: 'translateX(-50%)',
            }}
          >
            <NovaRobotIcon
              className="absolute"
              height={layout.iconArtworkHeight}
              width={layout.iconArtworkWidth}
              style={{
                left: layout.iconArtworkLeft,
                top: layout.iconArtworkTop,
              }}
            />
          </div>

          <motion.h2
            className="absolute z-10 m-0 text-center"
            data-testid="nova-empty-state-title"
            id={titleId}
            style={{
              top: layout.titleTop,
              left: 0,
              right: 0,
              margin: '0 auto',
              width: layout.titleWidth,
              color: '#262D46',
              fontFamily: 'Open Sans, sans-serif',
              fontSize: 17,
              fontWeight: layout.titleFontWeight,
              lineHeight: '24px',
            }}
            {...getFadeUpMotionProps(prefersReducedMotion, 0.5)}
          >
            {GREETING_TITLE}
          </motion.h2>

          <motion.p
            className="absolute z-10 m-0 text-center"
            data-testid="nova-empty-state-description"
            id={descriptionId}
            style={{
              top: layout.bodyTop,
              left: 0,
              right: 0,
              margin: '0 auto',
              width: layout.bodyWidth,
              color: '#546A83',
              fontFamily: 'Open Sans, sans-serif',
              fontSize: layout.bodyFontSize,
              fontWeight: 400,
              lineHeight: `${layout.bodyLineHeight}px`,
            }}
            {...getFadeUpMotionProps(prefersReducedMotion, 0.6)}
          >
            {message || DEFAULT_GREETING_MESSAGE}
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
}
